import React from 'react';
import { CheckIcon, InboxIcon, XIcon } from 'lucide-react';
import type { Navigate, PtoRequest } from '../../types/hr';
import { displayName, getEmployee } from '../../utils/employees';
import { formatDays, formatRange, relativeDay } from '../../utils/format';
import { policyName } from '../../utils/timeOff';
import { Avatar } from '../ui/Avatar';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { EmptyState } from '../ui/EmptyState';
interface RequestsQueueProps {
  requests: PtoRequest[];
  onDecide: (id: string, status: 'approved' | 'denied') => void;
  onNavigate: Navigate;
}
export function RequestsQueue({
  requests,
  onDecide,
  onNavigate
}: RequestsQueueProps) {
  const pending = requests.filter(r => r.status === 'pending');
  const decided = requests.filter(r => r.decidedInSession && (r.status === 'approved' || r.status === 'denied'));
  return <div className="space-y-6" data-id="element-1033" data-mp="32ikzjh5xd5e6">
      <Card title="Pending requests" subtitle={`${pending.length} awaiting decision`} icon={InboxIcon} bodyClassName="pb-2" data-id="element-1034" data-mp="jlc4tqple2k9">
        {pending.length === 0 ? <div className="px-6 pb-4" data-id="element-1035" data-mp="ojvqdq09xcc3">
            <EmptyState icon={InboxIcon} title="All caught up" description="There are no time off requests waiting for approval." data-id="element-1036" data-mp="9vixx30eabmc" />
          </div> : <RequestTable requests={pending} onDecide={onDecide} onNavigate={onNavigate} data-id="element-1037" data-mp="essssy6jzfhk" />}
      </Card>

      {decided.length > 0 && <Card title="Recently decided" subtitle="This session" icon={CheckIcon} bodyClassName="pb-2" data-id="element-1038" data-mp="5tnwzzmdm093">
          <RequestTable requests={decided} onNavigate={onNavigate} data-id="element-1039" data-mp="3n2awomz9yv9a" />
        </Card>}
    </div>;
}
interface RequestTableProps {
  requests: PtoRequest[];
  onDecide?: (id: string, status: 'approved' | 'denied') => void;
  onNavigate: Navigate;
}
function RequestTable({
  requests,
  onDecide,
  onNavigate
}: RequestTableProps) {
  return <div className="overflow-x-auto" data-id="element-1040" data-mp="1q992sbzc93x1">
      <table className="w-full min-w-[980px] text-left text-sm" data-id="element-1041" data-mp="1oslsyj484vyf">
        <thead data-id="element-1042" data-mp="1otwv6ei0tvnf">
          <tr className="border-y border-slate-100 bg-canvas/70 text-[11px] font-semibold uppercase tracking-wider text-muted" data-id="element-1043" data-mp="1oq90vd4r557n">
            <th scope="col" className="px-6 py-2.5" data-id="element-1044" data-mp="1etw7yhh16vdy">Employee</th>
            <th scope="col" className="px-6 py-2.5" data-id="element-1045" data-mp="1ewih2ljkliya">Dates</th>
            <th scope="col" className="px-6 py-2.5" data-id="element-1046" data-mp="1au5mmta9x7gt">Policy</th>
            <th scope="col" className="px-6 py-2.5" data-id="element-1047" data-mp="27itp2h9iam54">Note</th>
            <th scope="col" className="px-6 py-2.5 text-right" data-id="element-1048" data-mp="27ibj2sx1ktk0">Balance after</th>
            <th scope="col" className="px-6 py-2.5" data-id="element-1049" data-mp="22lbi2wf1ugzr">Submitted</th>
            <th scope="col" className="px-6 py-2.5 text-right" data-id="element-1050" data-mp="1xsrztm5f2xom">{onDecide ? 'Decision' : 'Status'}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100" data-id="element-1051" data-mp="1tqx2esqlx4g4">
          {requests.map(r => {
          const emp = getEmployee(r.employeeId);
          if (!emp) return null;
          const after = r.balanceBefore - r.days;
          return <tr key={r.id} className="transition-colors hover:bg-azure/[0.03]" data-id="element-1052" data-mp="808a2q255naz">
                <td className="px-6 py-3.5" data-id="element-1053" data-mp="82kx3hmranuw">
                  <button type="button" onClick={() => onNavigate({
                page: 'profile',
                employeeId: emp.id,
                tab: 'timeoff'
              })} className="flex items-center gap-3 text-left" data-id="element-1054" data-mp="d0cp0bnei9z3">
                    <Avatar name={displayName(emp)} seed={emp.id} size="sm" data-id="element-1055" data-mp="lzrfhb3kikjl" />
                    <span data-id="element-1056" data-mp="lxn11z81m352">
                      <span className="block font-medium text-navy hover:text-azure" data-id="element-1057" data-mp="qzm5lcfir63l">{displayName(emp)}</span>
                      <span className="block text-xs text-muted" data-id="element-1058" data-mp="vxdwq48cao6q">{emp.department}</span>
                    </span>
                  </button>
                </td>
                <td className="whitespace-nowrap px-6 py-3.5" data-id="element-1059" data-mp="10r9qsvlhfqwm">
                  <span className="block font-medium text-slate-800" data-id="element-1060" data-mp="14p7j47ajut48">{formatRange(r.start, r.end)}</span>
                  <span className="block text-xs text-muted" data-id="element-1061" data-mp="19r58lj9jmk5m">{formatDays(r.days)}</span>
                </td>
                <td className="px-6 py-3.5" data-id="element-1062" data-mp="1eoycu6rad439">
                  <Badge tone={r.policyId === 'newhire' ? 'navy' : 'blue'} data-id="element-1063" data-mp="111g3g4brp94l">{policyName(r.policyId)}</Badge>
                </td>
                <td className="max-w-[240px] px-6 py-3.5 text-xs italic text-slate-500" data-id="element-1064" data-mp="w1tn3m0n7crk">“{r.note}”</td>
                <td className="whitespace-nowrap px-6 py-3.5 text-right tabular-nums" data-id="element-1065" data-mp="1aqg4rudqsgly">
                  <span className={`font-semibold ${after < 0 ? 'text-rose-600' : 'text-navy'}`} data-id="element-1066" data-mp="15tpph99rhp1l">{after} days</span>
                  <span className="block text-[11px] text-muted" data-id="element-1067" data-mp="i3vgj2skckmm">from {r.balanceBefore}</span>
                </td>
                <td className="whitespace-nowrap px-6 py-3.5 text-slate-600" data-id="element-1068" data-mp="d5tvicbkub8i">{relativeDay(r.submitted)}</td>
                <td className="px-6 py-3.5 text-right" data-id="element-1069" data-mp="rz6sxegi1fr7">
                  {onDecide ? <div className="flex justify-end gap-2" data-id="element-1070" data-mp="n0vlfadofhc5">
                      <Button size="sm" variant="secondary" icon={XIcon} onClick={() => onDecide(r.id, 'denied')} data-id="element-1071" data-mp="23fndfm6wumkx">
                        Deny
                      </Button>
                      <Button size="sm" variant="primary" icon={CheckIcon} onClick={() => onDecide(r.id, 'approved')} data-id="element-1072" data-mp="2614krsl25is2">
                        Approve
                      </Button>
                    </div> : <Badge tone={r.status === 'approved' ? 'green' : 'red'} dot data-id="element-1073" data-mp="1x18165diza3v">
                      {r.status === 'approved' ? 'Approved' : 'Denied'}
                    </Badge>}
                </td>
              </tr>;
        })}
        </tbody>
      </table>
    </div>;
}